/* ------------------------------------------------------------------
 * Agenda.jsx – version mise à jour le 7 juillet 2025
 * ------------------------------------------------------------------
 * Corrections et ajouts demandés :
 * 1. Suppression du bouton "Voir" - clic direct en mode lecture
 * 2. Padding bottom pour les événements multi-jours
 * 3. Lexique réorganisé par thèmes en espagnol
 * 4. Icône scouting en vivo différenciée (🔴 au lieu de 🎥)
 * 5. Repositionnement des événements multi-jours comme les événements simples
 * 6. CORRECTION : Espacement vertical entre événements multi-jours
 * ------------------------------------------------------------------ */

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  ListItemText,
  Avatar,
  Menu,
  List,
  ListItem,
  ListItemIcon,
  ListItemText as ListText,
  useMediaQuery,
  useTheme,
  Paper,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ArrowBackIos,
  ArrowForwardIos,
  Add as AddIcon,
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  CloudDownload as CloudDownloadIcon,
  FilterList as FilterListIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  format,
  isSameDay,
  parseISO,
  setHours,
  setMinutes,
  addHours,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  startOfDay,
} from "date-fns";
import api from "../../api/axiosConfig";
import { es, fr, enUS } from "date-fns/locale";
import { format as formatDateFns } from "date-fns";
import EventModal from "./EventModal";

/* ------------------------------------------------------------------
 * Composant Avatars superposés
 * ------------------------------------------------------------------ */
function OverlappingAvatars({ participants }) {
  if (!participants || participants.length === 0) return null;
  const MAX_AVATARS = 3;
  const total = participants.length;
  const displayed = participants.slice(0, MAX_AVATARS);
  const remainder = total - displayed.length;
  return (
    <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
      {displayed.map((p, index) => (
        <Avatar
          key={p.id}
          src={
            p.id
              ? `${process.env.REACT_APP_MEDIA_URL}/users/${p.id}.png?${new Date().getTime()}`
              : ""
          }
          alt={`${p.nom} ${p.prenom}`}
          sx={{
            width: 24,
            height: 24,
            fontSize: "0.75rem",
            ml: index === 0 ? 0 : -1,
            border: "1px solid #fff",
          }}
        />
      ))}
      {remainder > 0 && (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#333",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.7rem",
            ml: -1,
            border: "1px solid #fff",
          }}
        >
          +{remainder}
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------
 * Composant d'informations supplémentaires d'un événement
 * ------------------------------------------------------------------ */
function EventExtraInfo({ event, showText = false, variant = "small" }) {
  const { t } = useTranslation();
  const getTransportIcon = (medio) => {
    const icons = {
      TREN: "🚂",
      AVION: "✈️",
      COCHE_CLUB: "🚗",
      COCHE_PERSONAL: "🚙",
      BUS: "🚌",
    };
    return icons[medio] || "🚗";
  };
  const getScoutingIcon = (tipo) => {
    const icons = {
      LIVE: "🔴",
      VIDEO: "📹",
    };
    return icons[tipo] || "📹";
  };
  const hasExtraInfo =
    event.ubicacion ||
    event.url ||
    event.medioTransporte ||
    event.tipoScouting ||
    (event.confirmado && event.confirmado !== ""); // ← MODIFICATION : seulement si confirmado a une valeur

  if (!hasExtraInfo) return null;
  const iconSize = variant === "large" ? 20 : variant === "medium" ? 16 : 14;
  const fontSize =
    variant === "large" ? "0.8rem" : variant === "medium" ? "0.75rem" : "0.65rem";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        flexWrap: "wrap",
      }}
    >
      {event.confirmado === "SI" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <CheckCircleIcon sx={{ fontSize: iconSize, color: "#4caf50" }} />
          {showText && (
            <Typography variant="caption" sx={{ color: "#fff", fontSize }}>
              {t('agenda.confirmed')}
            </Typography>
          )}
        </Box>
      )}
      {event.confirmado === "NO" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <CancelIcon sx={{ fontSize: iconSize, color: "#f44336" }} />
          {showText && (
            <Typography variant="caption" sx={{ color: "#fff", fontSize }}>
              {t('agenda.notConfirmed')}
            </Typography>
          )}
        </Box>
      )}
      {event.medioTransporte && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Typography sx={{ fontSize: iconSize }}>{getTransportIcon(event.medioTransporte)}</Typography>
          {showText && (
            <Typography variant="caption" sx={{ color: "#fff", fontSize }}>
              {event.medioTransporte.replace("_", " ").toLowerCase()}
            </Typography>
          )}
        </Box>
      )}
      {event.tipoScouting && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Typography sx={{ fontSize: iconSize }}>{getScoutingIcon(event.tipoScouting)}</Typography>
          {showText && (
            <Typography variant="caption" sx={{ color: "#fff", fontSize }}>
              {event.tipoScouting}
            </Typography>
          )}
        </Box>
      )}
      {event.ubicacion && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <LocationOnIcon sx={{ fontSize: iconSize, color: "#fff", opacity: 0.9 }} />
          {showText && (
            <Typography variant="caption" sx={{ color: "#fff", fontSize }}>
              {event.ubicacion}
            </Typography>
          )}
        </Box>
      )}
      {event.url && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <LinkIcon sx={{ fontSize: iconSize, color: "#fff", opacity: 0.9 }} />
          {showText && (
            <Typography variant="caption" sx={{ color: "#fff", fontSize }}>
              Enlace
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------
 * Icône « Ajouter au calendrier » (Google / ICS) – taille agrandie
 * ------------------------------------------------------------------ */
function formatICSDate(dateString) {
  const date = parseISO(dateString);
  return formatDateFns(date, "yyyyMMdd'T'HHmmss'Z'");
}
function AddToCalendarIcon({ event }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpenMenu = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);
  const googleCalendarUrl = (() => {
    const start = formatICSDate(event.startDate).replace(/Z$/, "");
    const end = formatICSDate(event.endDate).replace(/Z$/, "");
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      event.title
    )}&dates=${start}%2F${end}&details=${encodeURIComponent(event.description || "")}`;
  })();
  const generateICS = () => {
    const dtStart = formatICSDate(event.startDate);
    const dtEnd = formatICSDate(event.endDate);
    const title = event.title || "";
    const desc = event.description || "";
    return [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Agenda//App//ES",
      "BEGIN:VEVENT",
      `UID:${event.id || new Date().getTime()}@agenda.com`,
      `DTSTAMP:${formatDateFns(new Date(), "yyyyMMdd'T'HHmmss'Z'")}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${title.replace(/\n/g, "\\n")}`,
      `DESCRIPTION:${desc.replace(/\n/g, "\\n")}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
  };
  const handleDownloadICS = (e) => {
    e.stopPropagation();
    const icsContent = generateICS();
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${event.title || "evento"}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setAnchorEl(null);
  };
  return (
    <>
      <IconButton
        size="medium"
        onClick={handleOpenMenu}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{ ml: 0.5, backgroundColor: "rgba(255,255,255,0.25)" }}
      >
        <EventIcon sx={{ fontSize: 22 }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} onClick={(e) => e.stopPropagation()}>
        <List disablePadding>
          <ListItem button component="a" href={googleCalendarUrl} target="_blank" rel="noopener noreferrer">
            <ListItemIcon>
              <CalendarTodayIcon fontSize="small" />
            </ListItemIcon>
            <ListText primary={t('agenda.googleCalendar')} />
          </ListItem>
          <ListItem button onClick={handleDownloadICS}>
            <ListItemIcon>
              <CloudDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListText primary={t('agenda.downloadICS')} />
          </ListItem>
        </List>
      </Menu>
    </>
  );
}

/* ------------------------------------------------------------------
 * Variables d'environnement
 * ------------------------------------------------------------------ */
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const section = process.env.REACT_APP_SECTION;

/* ------------------------------------------------------------------
 * Composant principal : Agenda
 * ------------------------------------------------------------------ */
export default function Agenda() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Sélectionner la locale date-fns selon la langue de l'application
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
        return enUS;
      case 'es':
      default:
        return es;
    }
  };
  const dateLocale = getDateLocale();
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setCurrentUser(JSON.parse(storedUser));
  }, []);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(isMobile ? "list" : "month");
  const [events, setEvents] = useState([]);
  const [tags, setTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchTags();
    fetchGroups();
    fetchUsers();
  }, []);

  // Déclencher la récupération des événements quand currentUser change
  useEffect(() => {
    if (currentUser !== null) {
      fetchEvents();
    }
  }, [currentUser]);

  const fetchEvents = async () => {
    try {
      // Inclure l'ID et le rôle de l'utilisateur dans la requête
      const params = { section };
      if (currentUser) {
        params.userId = currentUser.id;
        params.userRole = currentUser.role;
      }

      const response = await api.get('/api/agenda/all', { params });
      const transformed = response.data.map((ev) => ({
        id: ev.id,
        title: ev.titre || "Sin título",
        description: ev.description || "",
        startDate: ev.date_debut,
        endDate: ev.date_fin,
        tagId: ev.tag?.id || null,
        color: ev.tag?.code_couleur || "#757575",
        groupes: ev.groupes || [],
        participants: ev.participants || [],
        created_by: ev.created_by?.id || null,
        confirmado: ev.confirmado, // Garde la valeur null telle quelle       
        tipoScouting: ev.tipo_scouting || null,
        url: ev.url || null,
        ubicacion: ev.ubicacion || null,
        medioTransporte: ev.medio_transporte || null,  

      }));
      setEvents(transformed);
    } catch (error) {
      console.error("Error al recuperar los eventos:", error);
    }
  };
  const fetchTags = async () => {
    try {
      const response = await api.get('/api/agenda_tags');
      setTags(response.data);
    } catch (error) {
      console.error("Error al recuperar las etiquetas:", error);
    }
  };
  const fetchGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      setGroups(response.data);
    } catch (error) {
      console.error("Error al recuperar los grupos:", error);
    }
  };
  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error("Error al recuperar los usuarios:", error);
    }
  };

  const handlePrev = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };
  const handleNext = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      if (selectedTagIds.length > 0 && ev.tagId) {
        if (!selectedTagIds.includes(ev.tagId)) return false;
      } else if (selectedTagIds.length > 0 && !ev.tagId) {
        return false;
      }
      if (selectedGroupIds.length > 0) {
        const evGroupIds = (ev.groupes || []).map((g) => g.id);
        const intersection = evGroupIds.filter((id) => selectedGroupIds.includes(id));
        if (intersection.length === 0) return false;
      }
      if (selectedUserIds.length > 0) {
        const evParticipantIds = (ev.participants || []).map((p) => p.id);
        const intersection = evParticipantIds.filter((id) => selectedUserIds.includes(id));
        if (intersection.length === 0) return false;
      }
      return true;
    });
  }, [events, selectedTagIds, selectedGroupIds, selectedUserIds]);

  const handleAddEvent = async (newEvent) => {
    try {
      if (!currentUser || !currentUser.id) return;

      // Ajouter 2 heures pour compenser le décalage
      const addTwoHours = (dateTimeStr) => {
        const date = new Date(dateTimeStr);
        date.setHours(date.getHours() + 2);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:00`;
      };

      const payload = {
        section,
        created_by: currentUser.id,
        titre: newEvent.title,
        description: newEvent.description,
        tag_id: newEvent.tagId,
        date_debut: addTwoHours(newEvent.startDate),
        date_fin: addTwoHours(newEvent.endDate),
        group_ids: newEvent.groupIds || [],
        participant_ids: newEvent.participantIds || [],
        confirmado: newEvent.confirmado,
        medio_transporte: newEvent.medioTransporte,
        tipo_scouting: newEvent.tipoScouting,
        url: newEvent.url,
        ubicacion: newEvent.ubicacion,
      };
      await api.post('/api/agenda', payload);
      await fetchEvents();
    } catch (error) {
      console.error("Error al crear un evento:", error);
    }
  };
  const handleUpdateEvent = async (updatedEvent) => {
    try {
      const groupIds = updatedEvent.groupIds ? updatedEvent.groupIds : (updatedEvent.groupes || []).map((g) => g.id);
      const participantIds = updatedEvent.participantIds
        ? updatedEvent.participantIds
        : (updatedEvent.participants || []).map((p) => p.id);

      // Ajouter 2 heures pour compenser le décalage
      const addTwoHours = (dateTimeStr) => {
        const date = new Date(dateTimeStr);
        date.setHours(date.getHours() + 2);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:00`;
      };

      const payload = {
        section,
        titre: updatedEvent.title,
        description: updatedEvent.description,
        date_debut: addTwoHours(updatedEvent.startDate),
        date_fin: addTwoHours(updatedEvent.endDate),
        tag_id: updatedEvent.tagId,
        group_ids: groupIds,
        participant_ids: participantIds,
        confirmado: updatedEvent.confirmado,
        medio_transporte: updatedEvent.medioTransporte,
        tipo_scouting: updatedEvent.tipoScouting,
        url: updatedEvent.url,
        ubicacion: updatedEvent.ubicacion,
      };
      await api.put(`/api/agenda/${updatedEvent.id}`, payload);
      await fetchEvents();
    } catch (error) {
      console.error("Error al actualizar el evento:", error);
    }
  };
  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/api/agenda/${eventId}`);
      setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
    } catch (error) {
      console.error("Error al eliminar el evento:", error);
    }
  };

  const handleEventClick = (ev) => {
    setSelectedEvent(ev);
    setModalOpen(true);
  };

  const renderView = () => {
    switch (view) {
      case "month":
        return (
          <MonthView
            date={currentDate}
            events={filteredEvents}
            onEventChange={handleUpdateEvent}
            onEventClick={handleEventClick}
          />
        );
      default:
        return <ListView date={currentDate} events={filteredEvents} onEventClick={handleEventClick} />;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 2, minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={handlePrev}>
              <ArrowBackIos />
            </IconButton>
            <Typography variant="h6" sx={{ mx: 2, minWidth: "150px", textAlign: "center" }}>
              {format(currentDate, "MMMM yyyy", { locale: dateLocale })}
            </Typography>
            <IconButton onClick={handleNext}>
              <ArrowForwardIos />
            </IconButton>
            <Button onClick={() => setCurrentDate(new Date())} variant="outlined" size="small">
              {t('agenda.today')}
            </Button>
          </Box>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => setDrawerOpen(true)} startIcon={<FilterListIcon />} variant="outlined">
                {t('agenda.filters')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedEvent(null);
                  setModalOpen(true);
                }}
              >
                {t('agenda.createEvent')}
              </Button>
            </Box>
          )}
          {isMobile && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton onClick={() => setDrawerOpen(true)} color="primary">
                <FilterListIcon />
              </IconButton>
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedEvent(null);
                  setModalOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
        <Paper elevation={1} sx={{ p: 1, mb: 2 }}>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            <Button variant={view === "month" ? "contained" : "outlined"} onClick={() => setView("month")} size="small">
              {t('agenda.month')}
            </Button>
            <Button variant={view === "list" ? "contained" : "outlined"} onClick={() => setView("list")} size="small">
              {t('agenda.list')}
            </Button>
          </Stack>
        </Paper>
        {/* Afficher un message pour les entrenadors */}
        {currentUser?.role?.toLowerCase() === 'entrenador' && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#fff3e0' }}>
            <Typography variant="body2" color="text.secondary">
              ℹ️ {t('agenda.coachInfoMessage')}
            </Typography>
          </Paper>
        )}

        <Box sx={{ mb: 2 }}>{renderView()}</Box>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            {tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.libelle}
                size="small"
                sx={{
                  backgroundColor: tag.code_couleur,
                  color: "#fff",
                  "& .MuiChip-label": { color: "#fff" },
                }}
              />
            ))}
            {tags.length > 0 && <Divider orientation="vertical" flexItem />}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventIcon sx={{ color: "#757575" }} />
              <Typography variant="caption" color="text.secondary">
                {t('agenda.exportToCalendar')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('agenda.iconLegend')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                {t('agenda.confirmationStatus')}
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleIcon sx={{ color: "#4caf50" }} />
                  <Typography variant="body2">{t('agenda.confirmed')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CancelIcon sx={{ color: "#f44336" }} />
                  <Typography variant="body2">{t('agenda.notConfirmed')}</Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                {t('agenda.transportMeans')}
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>🚂</Typography>
                  <Typography variant="body2">{t('agenda.train')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>✈️</Typography>
                  <Typography variant="body2">{t('agenda.plane')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>🚗</Typography>
                  <Typography variant="body2">{t('agenda.clubCar')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>🚙</Typography>
                  <Typography variant="body2">{t('agenda.personalCar')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>🚌</Typography>
                  <Typography variant="body2">{t('agenda.bus')}</Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                {t('agenda.scoutingType')}
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>🔴</Typography>
                  <Typography variant="body2">{t('agenda.liveScoutingIcon')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>📹</Typography>
                  <Typography variant="body2">{t('agenda.videoScoutingIcon')}</Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                {t('agenda.additionalInfo')}
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOnIcon />
                  <Typography variant="body2">{t('agenda.location')}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinkIcon />
                  <Typography variant="body2">{t('agenda.associatedLink')}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: 300, p: 2, pt: 10 } }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('agenda.filters')}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="filter-tag-label">{t('agenda.filterByTag')}</InputLabel>
            <Select
              labelId="filter-tag-label"
              multiple
              value={selectedTagIds}
              onChange={(e) =>
                setSelectedTagIds(typeof e.target.value === "string" ? e.target.value.split(",").map(Number) : e.target.value)
              }
              input={<OutlinedInput label={t('agenda.filterByTag')} />}
              renderValue={(selected) =>
                tags
                  .filter((t) => selected.includes(t.id))
                  .map((t) => t.libelle)
                  .join(", ")
              }
            >
              {tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Checkbox checked={selectedTagIds.includes(t.id)} />
                  <ListItemText primary={t.libelle} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="filter-group-label">{t('agenda.filterByGroups')}</InputLabel>
            <Select
              labelId="filter-group-label"
              multiple
              value={selectedGroupIds}
              onChange={(e) =>
                setSelectedGroupIds(typeof e.target.value === "string" ? e.target.value.split(",").map(Number) : e.target.value)
              }
              input={<OutlinedInput label={t('agenda.filterByGroups')} />}
              renderValue={(selected) =>
                groups
                  .filter((g) => selected.includes(g.id))
                  .map((g) => g.libelle)
                  .join(", ")
              }
            >
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  <Checkbox checked={selectedGroupIds.includes(g.id)} />
                  <ListItemText primary={g.libelle} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="filter-users-label">{t('agenda.filterByUsers')}</InputLabel>
            <Select
              labelId="filter-users-label"
              multiple
              value={selectedUserIds}
              onChange={(e) =>
                setSelectedUserIds(typeof e.target.value === "string" ? e.target.value.split(",").map(Number) : e.target.value)
              }
              input={<OutlinedInput label={t('agenda.filterByUsers')} />}
              renderValue={(selected) =>
                allUsers
                  .filter((u) => selected.includes(u.id))
                  .map((u) => `${u.prenom} ${u.nom}`)
                  .join(", ")
              }
            >
              {allUsers.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  <Checkbox checked={selectedUserIds.includes(u.id)} />
                  <ListItemText primary={`${u.prenom} ${u.nom}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Drawer>
        <EventModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          event={selectedEvent}
          onCreate={handleAddEvent}
          onUpdate={handleUpdateEvent}
          onDelete={handleDeleteEvent}
          tags={tags}
          groups={groups}
          users={allUsers}
          viewOnly={selectedEvent !== null}  // ← SOLUTION : mode lecture seulement si event existe
        />
      </Box>
    </DndProvider>
  );
}

/* ------------------------------------------------------------------
 * Vue « Mois » - AVEC CORRECTION DU CHEVAUCHEMENT
 * ------------------------------------------------------------------ */
function MonthView({ date, events, onEventChange, onEventClick }) {
  const { t, i18n } = useTranslation();

  // Sélectionner la locale date-fns selon la langue de l'application
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
        return enUS;
      case 'es':
      default:
        return es;
    }
  };
  const dateLocale = getDateLocale();
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  const startWeek = startOfWeek(start, { weekStartsOn: 1 });
  const endWeek = endOfWeek(end, { weekStartsOn: 1 });
  const days = [];
  let day = startWeek;
  while (day <= endWeek) {
    days.push(day);
    day = addDays(day, 1);
  }
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  // CORRECTION : Fonction améliorée pour éviter le chevauchement
  const calculateMultiDayEvents = () => {
    const multiDayEvents = [];

    events.forEach((ev) => {
      const evStart = parseISO(ev.startDate);
      const evEnd = parseISO(ev.endDate);

      // Créer des dates ajustées UNIQUEMENT pour la comparaison
      const adjustedStart = new Date(evStart);
      adjustedStart.setHours(adjustedStart.getHours() - 2);
      const adjustedEnd = new Date(evEnd);
      adjustedEnd.setHours(adjustedEnd.getHours() - 2);

      // Utiliser les dates ajustées pour déterminer si c'est multi-jours
      const startDay = startOfDay(adjustedStart);
      const endDay = startOfDay(adjustedEnd);

      // Si c'est multi-jours après ajustement, traiter l'événement
      if (startDay.getTime() !== endDay.getTime()) {
        weeks.forEach((week, weekIndex) => {
          const weekStart = week[0];
          const weekEnd = addDays(week[6], 1);

          // Utiliser les dates AJUSTÉES pour le positionnement correct
          if (adjustedStart < weekEnd && adjustedEnd >= weekStart) {
            const eventStartInWeek = adjustedStart < weekStart ? 0 : week.findIndex((d) => isSameDay(d, adjustedStart));
            const eventEndInWeek = adjustedEnd >= weekEnd ? 6 : week.findIndex((d) => isSameDay(d, adjustedEnd));
            const startCol = Math.max(0, eventStartInWeek);
            const endCol = eventEndInWeek === -1 ? 6 : eventEndInWeek;
            
            multiDayEvents.push({
              ...ev,
              weekIndex,
              startCol,
              endCol,
              width: endCol - startCol + 1,
              isStart: evStart >= weekStart,
              isEnd: evEnd < weekEnd,
            });
          }
        });
      }
    });
    
    // NOUVEAU : Assignation des positions verticales pour éviter les chevauchements
    weeks.forEach((week, weekIndex) => {
      const weekEvents = multiDayEvents.filter(ev => ev.weekIndex === weekIndex);
      
      // Trier par date de début pour un ordre cohérent
      weekEvents.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      
      // Assigner les positions verticales
      weekEvents.forEach((event, index) => {
        event.verticalPosition = index;
      });
    });
    
    return multiDayEvents;
  };

  const getSingleDayEventsForDay = (currentDay) => {
    const dayStart = currentDay;
    const dayEnd = addDays(currentDay, 1);
    return events.filter((ev) => {
      const evStart = parseISO(ev.startDate);
      const evEnd = parseISO(ev.endDate);

      // Créer des dates ajustées pour vérifier si c'est mono-jour
      const adjustedStart = new Date(evStart);
      adjustedStart.setHours(adjustedStart.getHours() - 2);
      const adjustedEnd = new Date(evEnd);
      adjustedEnd.setHours(adjustedEnd.getHours() - 2);

      // Vérifier si c'est un événement d'un seul jour après ajustement
      const startDay = startOfDay(adjustedStart);
      const endDay = startOfDay(adjustedEnd);
      const isSingleDay = startDay.getTime() === endDay.getTime();

      // Utiliser la date AJUSTÉE pour déterminer sur quel jour afficher l'événement
      // Car c'est la vraie date de l'événement (sans le décalage +2h du backend)
      const isInCurrentDay = isSameDay(startDay, currentDay);

      return isSingleDay && isInCurrentDay;
    });
  };

  const getMultiDayEventsForDay = (currentDay, weekIndex) => {
    return multiDayEvents.filter((ev) => {
      return ev.weekIndex === weekIndex && currentDay >= weeks[weekIndex][ev.startCol] && currentDay <= weeks[weekIndex][ev.endCol];
    });
  };

  const multiDayEvents = calculateMultiDayEvents();

  // Obtenir les libellés des jours selon la locale
  const getDayLabels = () => {
    const firstDayOfWeek = startOfWeek(new Date(), { weekStartsOn: 1, locale: dateLocale });
    return Array.from({ length: 7 }, (_, i) =>
      format(addDays(firstDayOfWeek, i), 'EEE', { locale: dateLocale })
    );
  };
  const dayLabels = getDayLabels();

  // CORRECTION : Espacement configurable entre les événements multi-jours
  const VERTICAL_SPACING = 25; // Espacement entre les événements multi-jours
  const EVENT_HEIGHT = 75; // Hauteur d'un événement
  const BASE_TOP_OFFSET = 35; // Offset de base depuis le haut

  return (
    <Paper elevation={1} sx={{ p: 1 }}>
      <Grid container sx={{ mb: 1 }}>
        {dayLabels.map((dow) => (
          <Grid item xs={12 / 7} key={dow}>
            <Box sx={{ p: 1, textAlign: "center", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {dow}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      {weeks.map((week, weekIndex) => (
        <Box key={weekIndex} sx={{ position: "relative", mb: 0 }}>
          <Grid container>
            {week.map((d, dayIndex) => {
              const isToday = isSameDay(d, new Date());
              const isCurrentMonth = d.getMonth() === date.getMonth();
              const singleDayEvents = getSingleDayEventsForDay(d);
              const multiDayEventsForThisDay = getMultiDayEventsForDay(d, weekIndex);
              const multiDayEventsCount = multiDayEvents.filter((ev) => ev.weekIndex === weekIndex).length;
              
              return (
                <Grid
                  item
                  xs={12 / 7}
                  key={dayIndex}
                  sx={{
                    // CORRECTION : Hauteur ajustée avec le nouvel espacement
                    minHeight: `${120 + multiDayEventsCount * (EVENT_HEIGHT + VERTICAL_SPACING)}px`,
                    border: "1px solid #e0e0e0",
                    backgroundColor: isToday ? "#e3f2fd" : isCurrentMonth ? "#fff" : "#fafafa",
                    p: 0.5,
                    position: "relative",
                  }}
                >
                  <DayDropZone
                    dayDate={d}
                    onDropEvent={(eventItem) => {
                      const oldStart = parseISO(eventItem.startDate);
                      const oldEnd = parseISO(eventItem.endDate);
                      const oldHour = oldStart.getHours();
                      const oldMin = oldStart.getMinutes();
                      const newLocalStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), oldHour, oldMin);
                      const durationMs = oldEnd - oldStart;
                      const newLocalEnd = new Date(newLocalStart.getTime() + durationMs);
                      onEventChange({
                        ...eventItem,
                        startDate: newLocalStart.toISOString(),
                        endDate: newLocalEnd.toISOString(),
                      });
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? "bold" : "normal",
                        color: isCurrentMonth ? "text.primary" : "text.secondary",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {format(d, "d")}
                    </Typography>

                    {singleDayEvents.map((ev, eventIndex) => (
                      <DraggableEvent key={ev.id} event={ev}>
                        <Card
                          sx={{
                            // CORRECTION : Position ajustée avec le nouvel espacement
                            mt: eventIndex === 0 ? `${multiDayEventsCount * (EVENT_HEIGHT + VERTICAL_SPACING) + 25}px` : 1,
                            p: 0.5,
                            cursor: "pointer",
                            backgroundColor: ev.color,
                            minHeight: "70px",
                            position: "relative",
                            zIndex: 5,
                            "&:hover": { opacity: 0.85 },
                          }}
                          onClick={() => onEventClick(ev)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                lineHeight: 1,
                                flex: 1,
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "0.85rem",
                                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                mr: 0.5,
                              }}
                            >
                              {ev.title}
                            </Typography>
                            <AddToCalendarIcon event={ev} />
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <EventExtraInfo event={ev} showText={false} variant="medium" />
                            <OverlappingAvatars participants={ev.participants} />
                          </Box>
                        </Card>
                      </DraggableEvent>
                    ))}
                  </DayDropZone>
                </Grid>
              );
            })}
          </Grid>
          
          {/* CORRECTION : Événements multi-jours avec positionnement vertical amélioré */}
          {multiDayEvents
            .filter((ev) => ev.weekIndex === weekIndex)
            .map((ev) => {
              const cellWidth = 100 / 7;
              const eventWidthPercent = ev.width * cellWidth;
              const startPosition = ev.startCol * cellWidth;
              
              // CORRECTION : Utiliser verticalPosition au lieu d'eventIndex
              const topPosition = BASE_TOP_OFFSET + (ev.verticalPosition || 0) * (EVENT_HEIGHT + VERTICAL_SPACING);

              return (
                <DraggableEvent key={`multi-${ev.id}-${weekIndex}`} event={ev}>
                  <Card
                    elevation={2}
                    sx={{
                      position: "absolute",
                      top: `${topPosition}px`, // ← CORRECTION : Position calculée avec espacement
                      left: `${startPosition}%`,
                      width: `${eventWidthPercent}%`,
                      height: `${EVENT_HEIGHT}px`,
                      paddingBottom: "10px",
                      backgroundColor: ev.color,
                      cursor: "pointer",
                      borderRadius: ev.isStart && ev.isEnd ? "6px" : ev.isStart ? "6px 0 0 6px" : ev.isEnd ? "0 6px 6px 0" : "0",
                      border: `2px solid ${ev.color}`,
                      "&:hover": { transform: "translateY(-1px)", boxShadow: 3 },
                      transition: "all 0.2s ease-in-out",
                      zIndex: 15,
                    }}
                    onClick={() => onEventClick(ev)}
                  >
                    <CardContent
                      sx={{
                        p: "6px 8px 10px 8px !important",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        {ev.isStart && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#fff",
                              fontWeight: "bold",
                              fontSize: "0.9rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                              flex: 1,
                              mr: 0.5,
                            }}
                          >
                            {ev.title}
                          </Typography>
                        )}
                        {!ev.isStart && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#fff",
                              fontSize: "0.9rem",
                              fontWeight: "bold",
                              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                              opacity: 0.8,
                              flex: 1,
                            }}
                          >
                            ···
                          </Typography>
                        )}
                        {ev.isStart && <AddToCalendarIcon event={ev} />}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <EventExtraInfo event={ev} showText={false} variant="large" />
                        {ev.participants && ev.participants.length > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
                            {ev.participants.slice(0, 2).map((p, index) => (
                              <Avatar
                                key={p.id}
                                src={
                                  p.id
                                    ? `${process.env.REACT_APP_MEDIA_URL}/users/${p.id}.png?${new Date().getTime()}`
                                    : ""
                                }
                                alt={`${p.nom} ${p.prenom}`}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: "0.65rem",
                                  ml: index === 0 ? 0 : -0.5,
                                  border: "1px solid #fff",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                }}
                              />
                            ))}
                            {ev.participants.length > 2 && (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  backgroundColor: "rgba(0,0,0,0.6)",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.65rem",
                                  ml: -0.5,
                                  border: "1px solid #fff",
                                  fontWeight: "bold",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                }}
                              >
                                +{ev.participants.length - 2}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </DraggableEvent>
              );
            })}
        </Box>
      ))}
    </Paper>
  );
}

/* ------------------------------------------------------------------
 * Vue « Liste » Modernisée avec Material UI
 * ------------------------------------------------------------------ */
function ListView({ date, events, onEventClick }) {
  const { t, i18n } = useTranslation();

  // Sélectionner la locale date-fns selon la langue de l'application
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
        return enUS;
      case 'es':
      default:
        return es;
    }
  };
  const dateLocale = getDateLocale();
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = addDays(start, 13);
  const allDays = eachDayOfInterval({ start, end });
  
  const eventsForDay = (day) => {
    const dayStart = day;
    const dayEnd = addDays(day, 1);
    return events.filter((ev) => {
      const evStart = parseISO(ev.startDate);
      const evEnd = parseISO(ev.endDate);
      return evStart < dayEnd && evEnd >= dayStart;
    });
  };

  const getEventExtraText = (event) => {
    const extras = [];
    if (event.ubicacion) extras.push(`📍 ${event.ubicacion}`);
    if (event.medioTransporte) {
      const transporteLabels = {
        TREN: `🚂 ${t('agenda.train')}`,
        AVION: `✈️ ${t('agenda.plane')}`,
        COCHE_CLUB: `🚗 ${t('agenda.clubCar')}`,
        COCHE_PERSONAL: `🚙 ${t('agenda.personalCar')}`,
        BUS: `🚌 ${t('agenda.bus')}`,
      };
      extras.push(transporteLabels[event.medioTransporte] || event.medioTransporte);
    }
    if (event.tipoScouting) {
      const scoutingLabels = {
        LIVE: `🔴 ${t('agenda.live')}`,
        VIDEO: `📹 ${t('agenda.video')}`,
      };
      extras.push(scoutingLabels[event.tipoScouting] || event.tipoScouting);
    }
    if (event.confirmado === "SI") extras.push(`✅ ${t('agenda.confirmed')}`);
    if (event.confirmado === "NO") extras.push(`❌ ${t('agenda.notConfirmed')}`);
    return extras.join(" • ");
  };

  const isToday = (day) => isSameDay(day, new Date());
  const isCurrentWeek = (day) => {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    return day >= weekStart && day <= weekEnd;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {allDays.map((day) => {
        const dailyEvents = eventsForDay(day);
        const todayClass = isToday(day);
        const currentWeekClass = isCurrentWeek(day);
        
        if (dailyEvents.length === 0) return null;

        return (
          <Paper 
            key={+day} 
            elevation={todayClass ? 3 : 1}
            sx={{ 
              overflow: 'hidden',
              border: todayClass ? '2px solid #1976d2' : 'none',
              backgroundColor: todayClass ? '#f3f8ff' : '#fff'
            }}
          >
            {/* En-tête du jour */}
            <Box 
              sx={{ 
                bgcolor: todayClass ? 'primary.main' : currentWeekClass ? 'primary.light' : 'grey.100',
                color: todayClass ? 'white' : currentWeekClass ? 'white' : 'text.primary',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {format(day, "EEEE d MMMM", { locale: dateLocale })}
                </Typography>
                {todayClass && (
                  <Chip
                    label={t('agenda.today').toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                )}
              </Box>
              <Chip
                label={t('agenda.eventsCount', { count: dailyEvents.length })}
                variant="outlined"
                size="small"
                sx={{ 
                  borderColor: todayClass ? 'rgba(255,255,255,0.5)' : 'current',
                  color: todayClass ? 'white' : 'inherit'
                }}
              />
            </Box>

            {/* Liste des événements */}
            <List disablePadding>
              {dailyEvents.map((ev, index) => {
                const startStr = parseISO(ev.startDate);
                const endStr = parseISO(ev.endDate);
                const timeStart = format(startStr, "HH:mm");
                const timeEnd = format(endStr, "HH:mm");
                const extraText = getEventExtraText(ev);

                // Soustraire 2 heures pour compenser le décalage ajouté lors de l'enregistrement
                const adjustedStart = new Date(startStr);
                adjustedStart.setHours(adjustedStart.getHours() - 2);
                const adjustedEnd = new Date(endStr);
                adjustedEnd.setHours(adjustedEnd.getHours() - 2);

                // Comparer uniquement les dates sans l'heure pour déterminer si c'est multi-jours
                const isMultiDay = !isSameDay(startOfDay(adjustedStart), startOfDay(adjustedEnd));

                return (
                  <React.Fragment key={ev.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: 'action.hover'
                        },
                        py: 2,
                        alignItems: 'flex-start'
                      }}
                      onClick={() => onEventClick(ev)}
                    >
                      {/* Indicateur de couleur et horaire */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          minWidth: 80,
                          mr: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 40,
                            bgcolor: ev.color,
                            borderRadius: 2,
                            mb: 1
                          }}
                        />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            color: ev.color,
                            textAlign: 'center',
                            lineHeight: 1.2
                          }}
                        >
                          {isMultiDay ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {t('agenda.multiDay')}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {timeStart}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {timeStart}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {timeEnd}
                              </Typography>
                            </Box>
                          )}
                        </Typography>
                      </Box>

                      {/* Contenu principal */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: 0.5,
                              flex: 1,
                              pr: 1
                            }}
                          >
                            {ev.title}
                          </Typography>
                          <AddToCalendarIcon event={ev} />
                        </Box>

                        {ev.description && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ mb: 1, lineHeight: 1.4 }}
                          >
                            {ev.description}
                          </Typography>
                        )}

                        {/* Informations supplémentaires sous forme de chips */}
                        {(extraText || ev.url) && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {extraText.split(' • ').map((info, idx) => (
                              <Chip
                                key={idx}
                                label={info}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: 24,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            ))}
                            {ev.url && (
                              <Chip
                                label={`🔗 ${t('agenda.link')}`}
                                size="small"
                                variant="filled"
                                color="primary"
                                clickable
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: 24,
                                  '& .MuiChip-label': { px: 1 }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(ev.url, "_blank");
                                }}
                              />
                            )}
                          </Box>
                        )}

                        {/* Groupes */}
                        {ev.groupes && ev.groupes.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center' }}>
                              {t('agenda.groups')}:
                            </Typography>
                            {ev.groupes.map((group) => (
                              <Chip
                                key={group.id}
                                label={group.libelle}
                                size="small"
                                sx={{ 
                                  bgcolor: 'grey.100',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            ))}
                          </Box>
                        )}

                        {/* Participants avec avatars modernisés */}
                        {ev.participants && ev.participants.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('agenda.participants')}:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <OverlappingAvatars participants={ev.participants} />
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {ev.participants.length} {ev.participants.length > 1 ? t('agenda.people') : t('agenda.person')}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                    {index < dailyEvents.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        );
      })}
      
      {/* Message si aucun événement */}
      {allDays.every(day => eventsForDay(day).length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('agenda.noEventsThisFortnight')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {format(start, "d MMMM", { locale: dateLocale })} - {format(end, "d MMMM yyyy", { locale: dateLocale })}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------
 * Composants Drag & Drop
 * ------------------------------------------------------------------ */
function DraggableEvent({ event, children }) {
  const [{ isDragging }, dragRef] = useDrag({
    type: "EVENT",
    item: event,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  return (
    <Box ref={dragRef} sx={{ opacity: isDragging ? 0.5 : 1, cursor: "move" }}>
      {children}
    </Box>
  );
}
function DayDropZone({ dayDate, children, onDropEvent }) {
  const [{ isOver }, dropRef] = useDrop({
    accept: "EVENT",
    drop: (item) => {
      onDropEvent(item);
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });
  return (
    <Box ref={dropRef} sx={{ backgroundColor: isOver ? "#e3f2fd" : "inherit", minHeight: "100%", p: 0.5 }}>
      {children}
    </Box>
  );
}
function HourDropZone({ slotStart, children, onDropEvent }) {
  const [{ isOver }, dropRef] = useDrop({
    accept: "EVENT",
    drop: (item) => {
      onDropEvent(item);
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });
  return <Box ref={dropRef} sx={{ backgroundColor: isOver ? "#e3f2fd" : "inherit", minHeight: "100%" }}>{children}</Box>;
}